import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import colorsOptions from '../../fundamentals/colors-styles/constants'
import { chipsStyleOptions, chipsImageStyleOptions } from './constants'

const ChipsActionComponent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 0px;
  display: none;
`

const ChipsButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const ChipsContentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
  padding: 0 0.25rem;
  height: inherit;
`

const ChipsBase = styled.button`
  &:disabled {
    pointer-events: none;
    opacity: '50%';
  }

  padding: 2px 4px 2px 2px;
  border: none;
  outline: none;
  display: inline-flex;

  justify-content: ${(props) => (props.fullWidth ? 'space-between' : 'start')};
  width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
  align-items: center;
  white-space: nowrap;
  border-radius: 60px;
  transition-property:
    color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
  gap: 0.5rem;

  button,
  svg,
  path {
    color: inherit;
    stroke: inherit;
  }

  span {
    padding: 0 4px;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    cursor: pointer;
    ${ChipsActionComponent} {
      transition: width 160ms ease-out;
      width: 100%;
      display: block;
    }
  }

  ${({ size = 'medium' }) => css`
    ${chipsStyleOptions[size]}
  `}

  ${(props) =>
    props.selected &&
    props.componentType === 'choice' &&
    css`
      ${ChipsActionComponent} {
        transition: width 160ms ease-out;
        width: 20px;
        display: block;
      }
    `}
  
  line-height: normal;
`

const ChipsIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ size = 'medium' }) => css`
    ${chipsImageStyleOptions[size]}
  `}
`

const ChoiceChip = styled(ChipsBase)`
  padding: 4px 8px;
`

function SelectedButton(props) {
  if (props.componentType === 'choice') {
    return <ChoiceChip {...props} />
  }
  return <ChipsBase {...props} />
}

const Minimal = styled(SelectedButton)`
  background-color: ${colorsOptions.greys['grey-10']};
  color: ${colorsOptions.greys['grey-100']};
  stroke: ${colorsOptions.greys['grey-100']};
  border: 1px solid ${colorsOptions.greys['grey-30']};

  &:hover {
    background-color: ${colorsOptions.greys['grey-20']};
  }
  &:focus {
    background-color: ${colorsOptions.greys['grey-20']};
    outline: none;
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-40']};
    border-color: ${colorsOptions.greys['grey-40']};
  }
  &[aria-selected='true'] {
    background-color: ${colorsOptions.greys['grey-30']};
    border-color: ${colorsOptions.greys['grey-40']};
  }
  &:disabled {
    color: ${colorsOptions.greys['grey-40']};
    stroke: ${colorsOptions.greys['grey-40']};
  }
`

const Strong = styled(SelectedButton)`
  background-color: ${colorsOptions.greys['grey-10']};
  color: ${colorsOptions.greys['grey-100']};
  stroke: ${colorsOptions.greys['grey-100']};
  border: 1px solid ${colorsOptions.greys['grey-90']};

  &:hover {
    background-color: ${colorsOptions.greys['grey-20']};
  }
  &:focus {
    background-color: ${colorsOptions.greys['grey-20']};
    outline: none;
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-40']};
  }

  &[aria-selected='true'] {
    background-color: ${colorsOptions.greys['grey-30']};
  }
  &:disabled {
    color: ${colorsOptions.greys['grey-40']};
    stroke: ${colorsOptions.greys['grey-40']};
    border-color: ${colorsOptions.greys['grey-40']};
  }
`

const Dark = styled(SelectedButton)`
  background-color: ${colorsOptions.greys['grey-70']};
  color: ${colorsOptions.greys.white};
  stroke: ${colorsOptions.greys.white};

  &:hover {
    background-color: ${colorsOptions.greys['grey-80']};
  }
  &:focus {
    background-color: ${colorsOptions.greys['grey-80']};
    outline: none;
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-100']};
  }
  &[aria-selected='true'] {
    background-color: ${colorsOptions.greys['grey-90']};
  }
  &:disabled {
    background-color: ${colorsOptions.greys['grey-70']};
  }
`

const Orange = styled(SelectedButton)`
  background-color: ${colorsOptions.docuToolsOrange['dt-10']};
  color: ${colorsOptions.greys['grey-100']};
  stroke: ${colorsOptions.greys['grey-100']};

  &:hover {
    background-color: ${colorsOptions.docuToolsOrange['dt-20']};
  }
  &:focus {
    background-color: ${colorsOptions.docuToolsOrange['dt-20']};
    outline: none;
  }
  &:active {
    background-color: ${colorsOptions.docuToolsOrange['dt-40']};
    color: ${colorsOptions.greys.white};
    stroke: ${colorsOptions.greys.white};
  }
  &[aria-selected='true'] {
    background-color: ${colorsOptions.docuToolsOrange.orange};
    color: ${colorsOptions.greys.white};
    stroke: ${colorsOptions.greys.white};
  }
  &:disabled {
    color: ${colorsOptions.greys['grey-40']};
    stroke: ${colorsOptions.greys['grey-40']};
  }
`

SelectedButton.propTypes = {
  componentType: PropTypes.oneOf(['input', 'choice'])
}

export {
  ChipsContentContainer,
  ChipsButtonContainer,
  ChipsActionComponent,
  ChipsIconContainer,
  Minimal,
  Strong,
  Dark,
  Orange
}
